<template>
  <Preloader v-if="loading" />
  <VRow v-else-if="errorMessage">
    <VCol cols="12">
      <VAlert type="error">
        {{ errorMessage }}
      </VAlert>
    </VCol>
  </VRow>
  <VRow v-else>
    <VCol cols="12" class="pt-0 pt-md-2">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="40"
        :vacancy="vacancy"
        :step="2"
      >
        {{ $t('step') }}
      </VacancyStepTitle>
      <div class="d-none d-md-block">
        <VRow>
          <VCol cols="12" lg="8" class="py-0">
            <p class="h5 mb-9">
              {{ $t('editQuestionsTip') }}
            </p>
          </VCol>
          <VCol cols="12" lg="4" class="text-right py-0">
            <VacancyPresetCopy load-vacancies />
          </VCol>
        </VRow>
        <VacancyPresets
          v-if="$vuetify.breakpoint.mdAndUp"
          @input="onSelect"
          @setAiInterviewer="setAiInterviewer"
          :vacancy="vacancy"
          :avatars="avatars"
          div
          class="pt-4"
        />
      </div>
      <div class="d-md-none pt-4">
        <div class="font-weight-medium mb-1">
          {{ $t('stepMobile') }}
        </div>
        <div class="text--secondary mb-3">
          {{ $t('editQuestionsTip') }}
        </div>
        <VContainer class="mobile-container grey lighten-5">
          <VacancyPresets
            v-if="$vuetify.breakpoint.lgAndDown"
            @input="onSelect"
            @setAiInterviewer="setAiInterviewer"
            :vacancy="vacancy"
            :presets="presets"
            :avatars="avatars"
          />
          <VacancyPresetCopy class="mt-3" />
        </VContainer>
      </div>
      <VRow>
        <VCol cols="12">
          <VBtn
            :to="{name: 'vacancy-store', params: { id: vacancyId } }"
            class="mr-auto mr-md-2"
            outlined
            color="success"
            dark
          >
            {{ $t('goBack') }}
          </VBtn>
          <VBtn
            v-if="canMoveNext"
            color="success"
            dark
            elevation="0"
            @click="next"
          >
            {{ $t('next') }} &rarr;
          </VBtn>
        </VCol>
      </VRow>
    </VCol>
  </VRow>
</template>

<script>
import {
  interviewPresetTypes, wizardSteps,
} from '@/enums';
import Preloader from '@/components/Preloader.vue';
import ValidationMixin from '@/mixins/ValidationMixin';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import InterviewPreset from '@/orm/models/Interview/InterviewPreset';
import Interviewer from '@/orm/models/Interview/Interviewer';
import VacancyPresets from '@/components/Vacancy/VacancyPresets.vue';
import VacancyPresetCopy from '@/components/Vacancy/VacancyPresetCopy';
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Wizard from '@/orm/models/Wizard/Wizard';
import useAvatars from '@/compositions/useAvatars';
import { onMounted } from '@vue/composition-api';

export default {
  name: 'VacancyStepPreset',
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  components: {
    VacancyStepTitle,
    VacancyPresetCopy,
    VacancyPresets,
    Preloader,
  },
  mixins: [ValidationMixin],
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const { getAvatars, avatars } = useAvatars();

    onMounted(async () => {
      await getAvatars();
    });

    return {
      avatars,
    };
  },
  data() {
    return {
      loading: true,
      interviewer: null,
      preset: null,
      activePreset: false,
    };
  },
  computed: {
    vacancy() {
      return Vacancy.query().find(this.vacancyId);
    },
    presets() {
      const presetOrder = Array.from(Object.values(interviewPresetTypes));
      return InterviewPreset.query().withAllRecursive()
        .all()
        .sort((a, b) => presetOrder.indexOf(a.type) - presetOrder.indexOf(b.type));
    },
    interviewers() {
      return Interviewer.query().all();
    },
    canMoveNext() {
      return this.state.presetId > 0 && this.state.interviewerId > 0;
    },
    state() {
      return Wizard.modelState();
    },
  },
  async created() {
    if (this.vacancy?.hasAiQuestions && this.vacancy?.isAiInterviewCompiling) {
      await this.setAiInterviewer(0);
    }
  },
  methods: {
    async prepare() {
      Wizard.setVacancyId(this.vacancyId).setWizardStep(wizardSteps.PRESET).setIsCompleted(false);
      await this.withLoading(
        async () => {
          await Wizard.fetchInterviewers();
          await Promise.all([
            Wizard.fetchData(true),
            Wizard.fetchPresets(),
            Wizard.fetchPresetsCategories(),
          ]);
        },
      );
      Wizard.setSelectedQuestions([]);
      await Wizard.saveWizardState();
    },
    async onSelect(payload) {
      if (payload === 'aiInterview') {
        Wizard.setIsAiTemplate(true);
        return;
      }
      await Wizard.changeInterviewer(payload.interviewer?.id);
      Wizard.setIsAiTemplate(false);
      Wizard.setPresetId(payload.preset?.id);
      this.preset = payload.preset;
      this.interviewer = payload.interviewer;
      if (payload.preset?.activePreset) {
        this.activePreset = true;
      }
      await this.next();
    },
    async next() {
      if (this.canMoveNext) {
        await Wizard.fetchScript();
        await Wizard.saveWizardState();
        await this.redirect();
      }
    },
    async redirect() {
      if (
        this.state.selectedQuestions.length > 0
        && this.preset.type !== interviewPresetTypes.COMMON
      ) {
        Wizard.setPrevStep(wizardSteps.PRESET);
        await Wizard.saveWizardState();
        await this.goToQuestions();
      } else {
        await this.goToWizard();
      }
    },
    async setAiInterviewer() {
      await this.$router.push({
        name: 'vacancy-ai-questions',
        params: {
          vacancyId: this.vacancy.$id,
        },
      });
    },
    async goToQuestions() {
      await this.$router.push({
        name: 'vacancy-questions',
        params: {
          vacancyId: this.state.vacancyId,
          interviewerId: this.state.interviewerId,
        },
        query: {
          activePreset: !!this.activePreset,
        },
      });
    },
    async goToWizard() {
      await this.$router.push({
        name: 'vacancy-wizard',
        params: {
          vacancyId: this.state.vacancyId,
          interviewerId: this.state.interviewerId,
          index: 0,
        },
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.prepare());
  },
  beforeRouteUpdate(to, from, next) {
    this.prepare();
    next();
  },
};
</script>

<style lang="sass" scoped>
.slide
  position: relative
  height: 344px
  border-radius: 20px
  flex-direction: column
  justify-content: space-between
</style>

<i18n locale="en" lang="yaml">
  create: Create custom interview template
  choose: Choose template by position
  vacancy: Job
  step: "Choose an interview template"
  stepMobile: Choose an interview template
  editQuestionsTip: You can edit the list of questions from the template in the next step.
  next: Next
  customScenario: Custom template
  customScenarioTip: Create custom template
  goBack: Go back
  pleaseWaitAiLoaderText: 'Artificial intelligence is in the process of selecting questions for your job.
  The expected wait time is 3-5 minutes. Please hold on. If necessary, you can go to other sections.
  The job will be available in the jobs section after completion of the preparation process..'
</i18n>

<i18n locale="ru" lang="yaml">
  create: Создайте свой шаблон интервью
  choose: Выберите шаблон по специальности
  vacancy: Вакансия
  step: "выберите шаблон интервью"
  stepMobile: Выберите шаблон интервью
  editQuestionsTip: Список вопросов из шаблона вы сможете отредактировать на следующем шаге.
  next: Далее
  customScenario: Свой сценарий
  customScenarioTip: Создайте свой собственный сценарий с чистого листа
  goBack: Назад
  pleaseWaitAiLoaderText: 'Искусственный интеллект в процессе подбора вопросов для вашей вакансии.
Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
</i18n>

<style lang="scss" scoped>
.mobile-container {
  width: calc(100% + 24px);
  margin-left: -12px;
}
</style>
